import React from 'react';
import { isNil } from 'lodash';
import { ColorIndicator, DateTimeCell } from '../../../../components';
import { dynamicSort } from '../../../../utils/sorting';
import { formatFloat, formatPercent } from '../../../../utils/number';

export const createRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        label: item.material,
      },
      {
        label: item.value ? formatFloat(item.value) : '0',
      },
      {
        label: formatPercent(item.share),
      },
    ],
  }));
};

export const createFryerCyclesRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);
  let preparedRows = sortedRows.filter((item) => item.total || item.perHour);

  if (preparedRows.length > 0) preparedRows = withAdditionalRowsFryerCycles(preparedRows);

  return preparedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: !item.additional ? (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ) : (
          ''
        ),
      },
      {
        label: !item.additional ? (
          item.material
        ) : (
          <div className="custom-table__bold-text">{item.material}</div>
        ),
      },
      {
        label: !item.additional ? (
          formatFloat(item.total)
        ) : (
          <div className="custom-table__bold-text">{formatFloat(item.total)}</div>
        ),
      },
      {
        label: !item.additional ? (
          formatFloat(item.perHour)
        ) : (
          <div className="custom-table__bold-text">{formatFloat(item.perHour)}</div>
        ),
      },
    ],
  }));
};

export const createEntreeChutesRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);
  let preparedRows = sortedRows.filter((item) => item.packages || item.quantity);

  return preparedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        paddingRight: 0,
        paddingLeft: 4,
        isArrow: true,
      },
      {
        label: item.material,
      },
      {
        label: formatFloat(item.packages),
      },
      {
        label: formatFloat(item.quantity),
      },
    ],
    items:
      item.shares?.map((share) => ({
        cells: [
          {
            label: '',
          },
          {
            label: '',
          },
          {
            label: share.packageType,
          },
          {
            label: share.packagesCount,
          },
          {
            label: share.quantity ? formatFloat(share.quantity) : '0',
          },
        ],
      })) || [],
  }));
};

export const createTaskDelayRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        label: item.material,
      },
      {
        label: item.totalDelay,
      },
      {
        label: item.highTaskDelay,
      },
      {
        label: item.lowTaskDelay,
      },
      {
        label: item.averageTaskDelay,
      },
      {
        label: item.delaysNumber,
      },
    ],
  }));
};

export const createStockOutRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        label: item.material,
      },
      {
        label: item.timeMinutes,
      },
      {
        label: item.incidentCount,
      },
    ],
  }));
};

export const createWasteRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        label: item.material,
      },
      {
        label: item.wasteWeight ? formatFloat(item.wasteWeight) : '0',
      },
      {
        label: item.cookedRatio ? formatPercent(item.cookedRatio) : '0',
      },
      {
        label: formatPercent(item.share),
      },
    ],
  }));
};

export const createContainersRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        paddingRight: 8,
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        paddingRight: 0,
        paddingLeft: 4,
        isArrow: true,
      },
      {
        label: item.material,
      },
      {
        label: item.value ? formatFloat(item.value) : '0',
      },
      {
        label: formatPercent(item.share),
      },
    ],
    items:
      item.containerShares?.map((share) => ({
        cells: [
          {
            label: '',
          },
          {
            label: '',
          },
          {
            label: share.containerName,
          },
          {
            label: share.materialsCount ? formatFloat(share.materialsCount) : '0',
          },
          {
            label: `${formatPercent(share.materialsShare)}%`,
          },
        ],
      })) || [],
  }));
};

export const createTraysRows = (rows, materials, sortModel) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.map((item, index) => ({
    id: index,
    cells: [
      {
        paddingRight: 8,
        label: (
          <ColorIndicator color={materials.find((material) => material.value === item.material)?.color} />
        ),
      },
      {
        paddingRight: 0,
        paddingLeft: 4,
        isArrow: true,
      },
      {
        label: item.material,
      },
      {
        label: item.value ? formatFloat(item.value) : '0',
      },
      {
        label: `${formatPercent(item.share)}%`,
      },
    ],
    items:
      item.trayShares?.map((share) => ({
        cells: [
          {
            label: '',
          },
          {
            label: '',
          },
          {
            label: share.trayName,
          },
          {
            label: share.materialsCount ? formatFloat(share.materialsCount) : '0',
          },
          {
            label: `${formatPercent(share.materialsShare)}%`,
          },
        ],
      })) || [],
  }));
};

export const createConveyorsRows = (rows, materials, sortModel, totals) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);
  
  return sortedRows.length > 0
    ? [
      ...sortedRows.map((item, index) => ({
        id: index,
        cells: [
          {
            paddingRight: 8,
            label: (
              <ColorIndicator
                color={materials.find((material) => material.value === item.materialName)?.color}
              />
            ),
          },
          {
            paddingRight: 0,
            paddingLeft: 4,
            isArrow: true,
          },
          {
            label: item.materialName,
          },
          {
            label: formatPercent(item.utilized),
          },
          {
            label: formatFloat(item.count),
          },
          {
            label: isNil(item.quantity) ? 'N/A' : formatFloat(item.quantity),
          },
          {
            label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
          },
        ],
      })),
      {
        id: sortedRows.length,
        unclickable: true,
        cells: [
          {
            paddingRight: 8,
            label: '',
          },
          {
            paddingRight: 0,
            paddingLeft: 4,
            isArrow: false,
          },
          {
            label: <div className="custom-table__bold-text">Summary:</div>,
          },
          {
            label: (
              <div className="custom-table__bold-text">{formatPercent(totals.utilized)}</div>
            ),
          },
          {
            label: <div className="custom-table__bold-text">{formatFloat(totals.pansDetected)}</div>,
          },
          {
            label: (
              <div className="custom-table__bold-text">
                {isNil(totals.quantity) ? 'N/A' : formatFloat(totals.quantity)}
              </div>
            ),
          },
          {
            label: (
              <div className="custom-table__bold-text">
                {isNil(totals.elapsedTime) ? 'N/A' : formatFloat(totals.elapsedTime)}
              </div>
            ),
          },
        ],
      },
    ]
    : [];
};

export const createInboundContainersRows = (rows) => {
  return rows.map((item, index) => ({
    id: `conveyorInbound-${index}`,
    cells: [
      {
        label: '',
        paddingRight: 8,
      },
      {
        label: '',
        paddingRight: 0,
        paddingLeft: 4,
      },
      {
        label: item.containerName,
      },
      {
        label: <DateTimeCell date={item.inboundTime} />,
      },
      {
        label: <DateTimeCell date={item.placedTime} />,
      },
      {
        label: isNil(item.quantity) ? 'N/A' : formatFloat(item.quantity),
      },
      {
        label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
      },
    ],
  }));
};

export const createOutboundContainersRows = (rows) => {
  return rows.map((item, index) => ({
    id: `conveyorOutbound-${index}`,
    cells: [
      {
        label: '',
        paddingRight: 8,
      },
      {
        label: '',
        paddingRight: 0,
        paddingLeft: 4,
      },
      {
        label: item.containerName,
      },
      {
        label: <DateTimeCell date={item.removedTime} />,
      },
      {
        label: <DateTimeCell date={item.outboundTime} />,
      },
      {
        label: isNil(item.quantity) ? 'N/A' : formatFloat(item.quantity),
      },
      {
        label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
      },
    ],
  }));
};

export const createPRSRows = (rows, materials, sortModel, totals = {}) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.length > 0
    ? [
        ...sortedRows.map((item, index) => ({
          id: index,
          cells: [
            {
              paddingRight: 8,
              label: (
                <ColorIndicator
                  color={materials.find((material) => material.value === item.materialName)?.color}
                />
              ),
            },
            {
              paddingRight: 0,
              paddingLeft: 4,
              isArrow: true,
            },
            {
              label: item.materialName,
            },
            {
              label: formatFloat(item.count),
            },
            {
              label: isNil(item.quantity) ? 'N/A' : formatFloat(item.quantity),
            },
            {
              label: formatPercent(item.share),
            },
            {
              label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
            },
          ],
        })),
        {
          id: sortedRows.length,
          unclickable: true,
          cells: [
            {
              paddingRight: 8,
              label: '',
            },
            {
              paddingRight: 0,
              paddingLeft: 4,
              isArrow: false,
            },
            {
              label: <div className="custom-table__bold-text">Summary:</div>,
            },
            {
              label: <div className="custom-table__bold-text">{formatFloat(totals.pansDetected)}</div>,
            },
            {
              label: (
                <div className="custom-table__bold-text">
                  {isNil(totals.quantity) ? 'N/A' : formatFloat(totals.quantity)}
                </div>
              ),
            },
            {
              label: <div className="custom-table__bold-text">100%</div>,
            },
            {
              label: (
                <div className="custom-table__bold-text">
                  {isNil(totals.averageTransitTime) ? 'N/A' : formatFloat(totals.averageTransitTime)}
                </div>
              ),
            },
          ],
        },
      ]
    : [];
};

export const createMTRSRows = (rows, materials, sortModel, summary = {}) => {
  const sortedRows = dynamicSort(rows, sortModel.sortBy, sortModel.orderDirection);

  return sortedRows.length > 0
    ? [
        ...sortedRows.map((item, index) => ({
          id: index,
          cells: [
            {
              paddingRight: 8,
              label: (
                <ColorIndicator
                  color={materials.find((material) => material.value === item.materialName)?.color}
                />
              ),
            },
            {
              paddingRight: 0,
              paddingLeft: 4,
              isArrow: true,
            },
            {
              label: item.materialName,
            },
            {
              label: formatFloat(item.count),
            },
            {
              label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
            },
          ],
        })),
        {
          id: sortedRows.length,
          unclickable: true,
          cells: [
            {
              paddingRight: 8,
              label: '',
            },
            {
              paddingRight: 0,
              paddingLeft: 4,
              isArrow: false,
            },
            {
              label: <div className="custom-table__bold-text">Summary:</div>,
            },
            {
              label: (
                <div className="custom-table__bold-text">{formatFloat(summary.pansDetected)}</div>
              ),
            },
            {
              label: (
                <div className="custom-table__bold-text">
                  {isNil(summary.averageTransitTime) ? 'N/A' : summary.averageTransitTime}
                </div>
              ),
            },
          ],
        },
      ]
    : [];
};

export const createPRSContainersRows = (rows) => {
  return rows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: '',
        paddingRight: 8,
      },
      {
        label: '',
        paddingRight: 0,
        paddingLeft: 4,
      },
      {
        label: item.containerName,
      },
      {
        label: <DateTimeCell date={item.placedTime} />,
      },
      {
        label: <DateTimeCell date={item.removedTime} />,
      },
      {
        label: isNil(item.quantity) ? 'N/A' : formatFloat(item.quantity),
      },
      {
        label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
      },
    ],
  }));
};

export const createMTRSTraysRows = (rows) => {
  return rows.map((item, index) => ({
    id: index,
    cells: [
      {
        label: '',
        paddingRight: 8,
      },
      {
        label: '',
        paddingRight: 0,
        paddingLeft: 4,
      },
      {
        label: item.containerName,
      },
      {
        label: <DateTimeCell date={item.placedTime} />,
      },
      {
        label: <DateTimeCell date={item.removedTime} />,
      },
      {
        label: isNil(item.elapsedTime) ? 'N/A' : formatFloat(item.elapsedTime),
      },
    ],
  }));
};

export const getChartData = (content, title, units) => {
  return [
    ['Name', 'value', { type: 'string', role: 'tooltip', p: { html: true } }],
    ...content.map((item) => {
      const formattedValue = formatFloat(item.value);
      const formattedUnits = units ? ` ${units}` : '';
      const formattedShare = ` (${formatPercent(item.share)}%)`;

      return [
        item.material,
        formattedValue,

        `<div class="chart-tooltip"><span>${
          item.material
        }</span>${`<div>${title}: <b>${formattedValue}${formattedUnits}${formattedShare}</b></div>`}</div>`,
      ];
    }),
  ];
};

export const getChartDataByKey = (content, key, title, units) => {
  return [
    ['Name', 'value', { type: 'string', role: 'tooltip', p: { html: true } }],
    ...content.map((item) => {
      const formattedValue = formatFloat(item[key]);
      const formattedUnits = units ? ` ${units}` : '';
      const formattedShare = item[`${key}Share`] ? ` (${formatPercent(item[`${key}Share`])}%)` : '';

      return [
        item.material,
        formattedValue,

        `<div class="chart-tooltip"><span>${
          item.material
        }</span>${`<div>${title}: <b>${formattedValue}${formattedUnits}${formattedShare}</b></div>`}</div>`,
      ];
    }),
  ];
};

export const getEmptyChartData = () => {
  return [
    ['Name', ''],
    ['Empty', 1],
  ];
};

export const formatSecondsToMinutes = (value) => {
  const minutes = value / 60;

  return parseFloat(minutes.toFixed(2));
};

export const formatMsToMinutes = (value) => {
  const minutes = value / 1000 / 60;

  return parseFloat(minutes.toFixed(2));
};

export const formatMsToSeconds = (value, fixed = 2) => {
  const minutes = value / 1000;

  return parseFloat(minutes.toFixed(fixed));
};

export const withAdditionalRowsFryerCycles = (rows) => {
  let maxTotal;
  let minTotal;
  let maxPerHour;
  let minPerHour;
  let sumTotal = 0;
  let sumPerHour = 0;

  const rowsCopy = [...rows];

  rows.forEach((row) => {
    if (isNil(maxTotal) || row.total > maxTotal) maxTotal = row.total;
    if (isNil(minTotal) || row.total < minTotal) minTotal = row.total;
    if (isNil(maxPerHour) || row.perHour > maxPerHour) maxPerHour = row.perHour;
    if (isNil(minPerHour) || row.perHour < minPerHour) minPerHour = row.perHour;
    sumTotal += row.total;
    sumPerHour += row.perHour;
  });

  const avgTotal = sumTotal / rows.length;
  const avgPerHour = sumPerHour / rows.length;

  rowsCopy.push({
    material: 'High Value',
    total: maxTotal,
    perHour: maxPerHour,
    additional: true,
  });

  rowsCopy.push({
    material: 'Low Value',
    total: minTotal,
    perHour: minPerHour,
    additional: true,
  });

  rowsCopy.push({
    material: 'Average Value',
    total: avgTotal,
    perHour: avgPerHour,
    additional: true,
  });

  return rowsCopy;
};
